import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Formik, Form } from "formik";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTran } from "@/hooks";
import { User, Eye, EyeOff } from "lucide-react";
import { useAuthStore } from "../hooks";
import { LoginSchema } from "../schemas";
import { AUTH_ENDPOINTS } from "../services/auth.endpoints";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common";
import { cn } from "@/lib/utils";

export const LoginForm = observer(({ variant = "default" }) => {
  const navigate = useNavigate();
  const { t } = useTran();
  const [showPassword, setShowPassword] = useState(false);
  const { loginForm, loading, login, applyAuthSuccess } = useAuthStore();
  const [searchParams] = useSearchParams();
  const usernamePrefill = searchParams.get("username") || "";
  const isPanel = variant === "panel";
  const isMobile = variant === "mobile";

  const handleLogin = async (values, { setSubmitting }) => {
    const loginUrl = `${AUTH_ENDPOINTS.LOGIN}${location.search || ""}`;
    const loginResult = await login(values, { url: loginUrl });

    if (loginResult.redirectTo) {
      window.location.href = loginResult.redirectTo;
      return;
    }

    const { user, accessToken, refreshTokenExpiresAt } = loginResult || {};
    if (user?.email2FAEnabled) {
      if (user?.isNew) {
        applyAuthSuccess({
          user,
          accessToken,
          refreshTokenExpiresAt,
        });
        return navigate(PATHS.CHANGE_PASSWORD);
      }
      navigate(PATHS.VERIFY_2FA_CODE, {
        state: { email: user.email },
      });

      return;
    }
    setSubmitting(false);

    // KYN - Commented out the redirection logic for now, as it will be handled in the PublicRoute component after authentication state is updated. This avoids potential issues with navigating before the auth state is fully updated.
    //   if (loginResult) {
    //     const delegationOnlyAccess = Boolean(
    //       loginResult?.user?.delegationOnlyAccess,
    //     );

    //     const from = location.state?.from;
    //     console.log("Login successful, delegationOnlyAccess:", delegationOnlyAccess,from);
    //     // const redirectPath =
    //     //   from?.pathname && String(from.pathname).startsWith("/")
    //     //     ? `${from.pathname}${from.search || ""}${from.hash || ""}`
    //     //     : delegationOnlyAccess
    //     //       ? PATHS.ESG_DELEGATED_ENTRY
    //     //       : PATHS.DASHBOARD;

    //     //navigate(redirectPath, { replace: true });
    //     //navigate(PATHS.DASHBOARD);
    //   }
  };

  return (
    <Formik
      initialValues={{ usernameOrEmail: usernamePrefill, password: "" }}
      validationSchema={LoginSchema}
      onSubmit={handleLogin}
    >
      {({ isSubmitting, errors, touched, values, handleChange }) => (
        <Form
          className={cn(
            "mx-auto w-full",
            isPanel
              ? "max-w-[308px] space-y-4"
              : isMobile
                ? "max-w-md space-y-4"
                : "max-w-md space-y-3 px-1 sm:px-2 md:px-4 lg:px-6",
          )}
        >
          <h1
            className={cn(
              "text-center font-semibold text-foreground",
              isPanel
                ? "text-[34px] leading-none"
                : isMobile
                  ? "text-3xl"
                  : "text-2xl sm:text-3xl",
            )}
          >
            {t("Login")}
          </h1>

          {!isPanel ? (
            <p
              className={cn(
                "text-center text-muted-foreground",
                isMobile ? "text-sm" : "text-sm",
              )}
            >
              {t("Enter your account details to continue.")}
            </p>
          ) : null}

          {/* Username or Email */}
          <FormField
            name="usernameOrEmail"
            placeholder={t("Username or Email")}
            value={values.usernameOrEmail}
            onChange={handleChange}
            error={touched.usernameOrEmail && errors.usernameOrEmail}
            rightIcon={<User className="h-5 w-5" />}
            className="space-y-1"
            inputClassName={cn(
              isPanel && "h-11 rounded-[10px] border-[#d9dde7] text-[15px]",
            )}
          />

          {/* Password */}
          <FormField
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("Password")}
            value={values.password}
            onChange={handleChange}
            error={touched.password && errors.password}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            }
            className="space-y-1"
            rightIconClassName="top-[60%]"
            inputClassName={cn(
              isPanel && "h-11 rounded-[10px] border-[#d9dde7] text-[15px]",
            )}
          />

          {/* Server Error */}
          {loginForm?.serverError && (
            <p className="text-xs text-red-600">{loginForm.serverError}</p>
          )}

          {/* Forgot Password */}
          <div className="text-right">
            <Link
              to={PATHS.FORGOT_PASSWORD}
              className={cn("text-primary hover:underline text-sm")}
            >
              {t("Forgot Password")}
            </Link>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || loading}
            className={cn(
              "w-full rounded-full",
              isPanel
                ? "mt-1 h-11 bg-[#18b57c] text-white hover:bg-[#15a670]"
                : "bg-secondary hover:bg-secondary/90",
            )}
          >
            {isSubmitting || loading ? t("Logging in...") : t("Login")}
          </Button>
        </Form>
      )}
    </Formik>
  );
});
